import {FC, useEffect, useMemo, useState} from "react";
import {ICalendarFilter, IGetCalendarFilterOptionsResponse} from "../../../entities/calendar";
import {useGlobalMessageActions} from "../../../entities/globalMessage";
import {IStudentData} from "../../../entities/student";
import {CalendarDay} from "../../../widgets/calendarDay";
import {CalendarMonth} from "../../../widgets/calendarMonth";
import classes from './studentLessonsCalendar.module.scss'

interface IProps {
    student: IStudentData;
    tutorUserId?: number;
    tutorFullName?: string;
    hideTitle?: boolean;
}

const getStudentFullName = (student: IStudentData): string => {
    return `${student.last_name} ${student.first_name} ${student.middle_name}`.trim()
}

const getTgAccountTitle = (username: string): string => {
    if(!username) return ''

    return username.startsWith('@') ? username : `@${username}`
}

const isTodayOrPastMsk = (date: Date): boolean => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date())
    const getPart = (type: string) => parts.find(part => part.type === type)?.value || ''
    const todayMsk = `${getPart('year')}-${getPart('month')}-${getPart('day')}`
    const selectedDate = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')

    return selectedDate <= todayMsk
}

export const StudentLessonsCalendar: FC<IProps> = ({
    student,
    tutorUserId,
    tutorFullName,
    hideTitle = false,
}) => {

    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const {setGlobalMessage} = useGlobalMessageActions()

    const tgAccountId = student.work_tg_ids?.[0] ? String(student.work_tg_ids[0]) : ''
    const resolvedTutorUserId = Number(tutorUserId || student.user_id || 0)
    const resolvedTutorFullName = tutorFullName || student.tutor_name || `Исполнитель #${resolvedTutorUserId}`
    const resolvedTgAccountTitle = getTgAccountTitle(student.work_tg_name || '')
    const calendarDescription = [
        getStudentFullName(student),
        resolvedTgAccountTitle,
        'время по МСК',
    ].filter(Boolean).join(' · ')

    const missingCalendarFields = [
        !resolvedTutorUserId && 'user_id',
    ].filter(Boolean).join(', ')

    useEffect(() => {
        if(!missingCalendarFields) return

        setGlobalMessage({
            message: `Недостаточно данных для календаря: ${missingCalendarFields}`,
            type: 'error',
        })
    }, [missingCalendarFields, setGlobalMessage])

    const filter = useMemo<ICalendarFilter>(() => ({
        tgAccountIds: [],
        userIds: resolvedTutorUserId ? [String(resolvedTutorUserId)] : [],
        studentIds: student.id ? [String(student.id)] : [],
        tagIds: [],
        includeCancelled: true,
        includeRescheduled: true,
    }), [resolvedTutorUserId, student.id])

    const filterOptions = useMemo<IGetCalendarFilterOptionsResponse>(() => ({
        viewer: {
            userId: '',
            roles: ['ACTOR_ROLE_ADMIN'],
        },
        tgAccounts: tgAccountId
            ? [{
                id: tgAccountId,
                username: student.work_tg_name || '',
                title: resolvedTgAccountTitle || student.work_tg_name || '',
            }]
            : [],
        tutors: resolvedTutorUserId
            ? [{
                id: String(resolvedTutorUserId),
                userId: String(resolvedTutorUserId),
                fullName: resolvedTutorFullName,
                tgAccountId,
            }]
            : [],
        students: student.id
            ? [{
                id: String(student.id),
                fullName: getStudentFullName(student),
                userId: String(resolvedTutorUserId),
                tgAccountId: '',
            }]
            : [],
        tags: [],
    }), [resolvedTutorFullName, resolvedTutorUserId, resolvedTgAccountTitle, student, tgAccountId])

    if(missingCalendarFields) return null

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    {!hideTitle && <h2 className={classes.title}>Календарь занятий</h2>}
                    <p className={classes.subtitle}>
                        {calendarDescription}
                    </p>
                </section>
            </section>

            {selectedDate
                ?
                <CalendarDay
                    date={selectedDate}
                    filter={filter}
                    filterOptions={filterOptions}
                    omitUserIds={isTodayOrPastMsk(selectedDate)}
                    backTitle="К месяцу"
                    onBack={() => setSelectedDate(null)}
                />
                :
                <CalendarMonth
                    filter={filter}
                    omitUserIds
                    onSelectDate={setSelectedDate}
                />
            }
        </section>
    )
}
